import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet as WalletIcon, Info, Loader2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { COMMISSION_STATE_META, eur } from "@/lib/constants";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Wallet() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [payout, setPayout] = useState(null);
  const [form, setForm] = useState({ full_name: "", fiscal_code: "", vat_number: "", address: "", iban: "", account_holder: "", email: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/wallet").then((r) => setItems(r.data.items)).catch(() => {});
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/payouts/me").then((r) => {
      setPayout(r.data);
      if (r.data && r.data.status && r.data.status !== "DA_COMPLETARE") {
        setForm({
          full_name: r.data.full_name || "",
          fiscal_code: r.data.fiscal_code || "",
          vat_number: r.data.vat_number || "",
          address: r.data.address || "",
          iban: r.data.iban || "",
          account_holder: r.data.account_holder || "",
          email: r.data.email || "",
        });
      }
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const hasDue = items.some((i) => ["maturato", "in_pagamento"].includes(i.state));

  const submitPayout = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payouts", form);
      toast.success("Dati di pagamento salvati.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const c = summary?.commissions || { potenziale: 0, maturato: 0, in_pagamento: 0, pagato: 0 };

  return (
    <DashboardLayout title="Il mio merito fidelizzazione">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex gap-3 mb-6">
        <Info className="h-5 w-5 text-slate-400 shrink-0" />
        <p className="text-xs text-slate-500 leading-relaxed">
          Un merito fidelizzazione <strong>potenziale</strong> non è denaro già guadagnato. Il merito fidelizzazione viene
          riconosciuto solo quando si verificano le condizioni previste dal programma e passa a
          <strong> maturato</strong>.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Potenziali", c.potenziale, "potenziale"],
          ["Maturati", c.maturato, "maturato"],
          ["In pagamento", c.in_pagamento, "in_pagamento"],
          ["Pagati", c.pagato, "pagato"],
        ].map(([label, val, key]) => {
          const m = COMMISSION_STATE_META[key];
          return (
            <div key={key} className={`rounded-xl border p-5 ${m.bg} ring-1 ${m.ring}`} data-testid={`wallet-${key}`}>
              <p className="text-sm text-slate-600">{label}</p>
              <p className={`mt-2 font-display text-2xl font-semibold ${m.color}`}>{eur(val)}</p>
            </div>
          );
        })}
      </div>

      <h2 className="font-display text-lg font-semibold text-slate-900 mt-10 mb-4">Dettaglio merito fidelizzazione</h2>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">
          <WalletIcon className="h-9 w-9 mx-auto text-slate-300" />
          <p className="mt-3">Nessun merito fidelizzazione ancora.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {items.map((it, i) => {
            const m = COMMISSION_STATE_META[it.state];
            return (
              <motion.div key={it.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                className="flex items-center justify-between px-5 py-4 border-b border-slate-100 last:border-0">
                <div>
                  <p className="font-mono text-sm font-semibold text-slate-900">{it.reference}</p>
                  <p className="text-xs text-slate-400">{it.comune || "—"}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${m.bg} ${m.color}`}>{m.label}</span>
                  <span className="font-display font-semibold text-slate-900 w-24 text-right">
                    {it.amount != null ? eur(it.amount) : (it.state === "potenziale" ? "da definire" : "—")}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Payout data */}
      <div className="mt-10">
        <h2 className="font-display text-lg font-semibold text-slate-900 mb-1">Dati di pagamento</h2>
        <p className="text-sm text-slate-500 mb-4">
          {hasDue ? "Hai un merito fidelizzazione esigibile: completa i dati per il pagamento." : "Puoi precompilare i dati per essere pronto quando maturerà."}
          {payout?.status && payout.status !== "DA_COMPLETARE" && <span className="ml-2 inline-flex rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">Stato: {payout.status.replace(/_/g, " ")}</span>}
        </p>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 mb-4" data-testid="fiscal-notice">
          <Info className="h-5 w-5 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Codice fiscale e IBAN sono obbligatori</strong> prima della liquidazione del merito fidelizzazione
            (Art. 7 dei Termini e Condizioni). Senza questi dati il pagamento resta sospeso.
          </p>
        </div>
        <form onSubmit={submitPayout} className="rounded-2xl border border-slate-200 bg-white p-6 grid gap-4 sm:grid-cols-2">
          <PField label="Nome e cognome / Ragione sociale" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="p-fullname" />
          <PField label="Intestatario conto" required value={form.account_holder} onChange={(v) => setForm({ ...form, account_holder: v })} testid="p-holder" />
          <PField label="Codice fiscale" required value={form.fiscal_code} onChange={(v) => setForm({ ...form, fiscal_code: v })} testid="p-cf" />
          <PField label="Partita IVA (se applicabile)" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} testid="p-piva" />
          <PField label="IBAN" required value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} testid="p-iban" />
          <PField label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="p-email" />
          <div className="sm:col-span-2"><PField label="Indirizzo" required value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="p-address" /></div>
          <div className="sm:col-span-2">
            <button disabled={saving} data-testid="payout-submit-btn" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva dati di pagamento
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

function PField({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none" />
    </div>
  );
}
